import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { products } from "@/data/products";
import { BlurIn, StaggerContainer, StaggerItem } from "./ScrollAnimations";

const showcase = [
  { ...products.find((p) => p.id === "perfect-mesh-blue"), tag: "Bestseller" },
  { ...products.find((p) => p.id === "project-blue"), tag: "Executive" },
  { ...products.find((p) => p.id === "embassy"), tag: "Premium" },
  { ...products.find((p) => p.id === "wings-blue"), tag: "Compact" },
  { ...products.find((p) => p.id === "custom-notebook"), tag: "Customisable" },
];

const Card = ({ item, className, imgClass }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, scale: 0.9, filter: "blur(6px)" },
      visible: {
        opacity: 1,
        scale: 1,
        filter: "blur(0px)",
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
      },
    }}
    className={className}
  >
    <Link
      to={`/shop/${item.id}`}
      data-testid={`showcase-${item.id}`}
      className="group relative block h-full rounded-2xl overflow-hidden bg-[#F3F1ED]"
    >
      <motion.img
        src={item.images[0]}
        alt={item.name}
        className={`w-full h-full object-cover transition-transform duration-700 ease-out ${imgClass || ""}`}
        whileHover={{ scale: 1.08 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <motion.span
        initial={{ opacity: 0, x: -10 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
        className="absolute top-4 left-4 text-[10px] tracking-[0.15em] uppercase font-semibold px-3 py-1 rounded-full bg-white/90 text-[#7A1A31] backdrop-blur-sm"
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        {item.tag}
      </motion.span>
      <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
        <span
          className="text-[10px] tracking-[0.12em] uppercase text-[#C8A23A] font-medium block mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          {item.categoryLabel}
        </span>
        <h3
          className="text-white text-lg drop-shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
        >
          {item.name}
        </h3>
      </div>
    </Link>
  </motion.div>
);

export const ShopSection = () => {
  return (
    <section id="products" data-testid="shop-section" className="py-24 sm:py-32 bg-white relative">
      <div className="container mx-auto px-6 sm:px-8 max-w-7xl">
        <BlurIn className="text-center max-w-2xl mx-auto mb-16">
          <span
            className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C8A23A] mb-4 block"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Our Collection
          </span>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#1A1A1A] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="shop-heading"
          >
            Crafted for the <span className="text-[#7A1A31]">Year Ahead</span>
          </h2>
          <p
            className="text-base text-[#6B7280] leading-relaxed"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
          >
            Premium diaries, planners & notebooks — designed to impress.
          </p>
        </BlurIn>

        <StaggerContainer
          className="grid grid-cols-2 lg:grid-cols-12 gap-4 auto-rows-[220px] sm:auto-rows-[260px]"
          staggerDelay={0.12}
        >
          <Card item={showcase[0]} className="col-span-2 lg:col-span-7 row-span-2" imgClass="object-cover" />
          <Card item={showcase[1]} className="col-span-1 lg:col-span-5 row-span-1" imgClass="object-cover" />
          <Card item={showcase[2]} className="col-span-1 lg:col-span-5 row-span-1" imgClass="object-cover" />
          <Card item={showcase[3]} className="col-span-1 lg:col-span-6 row-span-1" imgClass="object-cover" />
          <Card item={showcase[4]} className="col-span-1 lg:col-span-6 row-span-1" imgClass="object-cover" />
        </StaggerContainer>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-14 text-center"
        >
          <Link
            to="/shop"
            data-testid="view-all-shop"
            className="group inline-flex items-center gap-3 bg-[#7A1A31] text-white px-10 py-4 rounded-full hover:bg-[#5C162E] transition-all duration-300 text-sm font-medium tracking-widest uppercase shadow-lg shadow-[#7A1A31]/15"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            <motion.span whileHover={{ x: 0 }}>Explore All {products.length} Products</motion.span>
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
